import React from 'react';
import { FormControl, FormHelperText, MenuItem, Select, TextField } from '@mui/material';
import styles from './PatientInsuranceForm.module.css';

const CARD_NUMBER_MAX = 40;
const HOLDER_MAX = 120;
const NOTES_MAX = 300;

const selectSx = { borderRadius: '10px' };

/** Estado do formulário a partir de uma carteirinha existente (ou vazio). */
export const toInsuranceFormState = (initial) => ({
    provider_id: initial?.provider_id ? String(initial.provider_id) : '',
    plan_id: initial?.plan_id ? String(initial.plan_id) : '',
    card_number: initial?.card_number || '',
    holder_name: initial?.holder_name || '',
    valid_until: initial?.valid_until || '',
    notes: initial?.notes || ''
});

/** Algum campo preenchido (o cadastro de pessoa nova só cria a carteirinha nesse caso). */
export const isInsuranceFormFilled = (form) => Object.values(form || {}).some((value) => String(value || '').trim() !== '');

/**
 * Operadoras para o seletor. Na edição a operadora pode estar inativa (fora de
 * CompanyConfig): mantém a opção só para o valor atual aparecer, sem oferecer
 * a inativa para outras carteirinhas.
 */
export const buildInsuranceProviderOptions = (providerList, initial) => {
    const list = (providerList || []).map((provider) => ({
        id: String(provider.Id),
        label: provider.Label,
        active: 1,
        plans: (provider.Plans || []).map((plan) => ({ id: String(plan.Id), label: plan.Label }))
    }));
    if (initial?.provider_id && !list.some((provider) => provider.id === String(initial.provider_id))) {
        list.push({
            id: String(initial.provider_id),
            label: `${initial.provider_name || 'Operadora'} (inativa)`,
            name: initial.provider_name || 'Operadora',
            active: 0,
            plans: initial.plan_id ? [{ id: String(initial.plan_id), label: initial.plan_name || 'Plano' }] : []
        });
    }
    return list;
};

/** Erros por campo ({} quando está tudo certo). */
export const validateInsuranceForm = (form) => {
    const next = {};
    if (!form.provider_id) next.provider_id = 'Escolha a operadora';
    if (!form.card_number.trim()) next.card_number = 'Informe o número da carteirinha';
    if (form.valid_until && !/^\d{4}-\d{2}-\d{2}$/.test(form.valid_until)) next.valid_until = 'Data inválida';
    return next;
};

/** Corpo de SetPatientInsurance / UpdatePatientInsurance. */
export const buildInsurancePayload = (form, patientId) => ({
    patientId,
    provider_id: form.provider_id,
    plan_id: form.plan_id || '',
    card_number: form.card_number.trim(),
    holder_name: form.holder_name.trim(),
    valid_until: form.valid_until || '',
    notes: form.notes.trim()
});

/**
 * Campos da carteirinha, sem formulário nem botões: usados pelo
 * PatientInsuranceForm e embutidos no popup de cadastro de pessoa nova.
 * Controlado: quem usa guarda `form` e `errors`.
 */
export default function PatientInsuranceFields({
    form, errors = {}, options, disabled = false, showMore = true, compact = false,
    idPrefix, autoFocusNumber = false, onProviderChange, onFieldChange
}) {
    const plans = (options.find((provider) => provider.id === form.provider_id) || {}).plans || [];
    const providerLabelId = `${idPrefix}-provider-label`;
    const planLabelId = `${idPrefix}-plan-label`;

    return (
        <div className={styles.fieldsContainer}>
            <div className={styles.grid}>
                <div className={`${styles.field} ${plans.length === 0 ? styles.full : ''}`}>
                    <span className={styles.label} id={providerLabelId}>Operadora <span aria-hidden="true">*</span></span>
                    <FormControl size="small" fullWidth error={!!errors.provider_id} disabled={disabled}>
                        <Select
                            labelId={providerLabelId}
                            value={form.provider_id}
                            onChange={(e) => onProviderChange(e.target.value)}
                            displayEmpty
                            sx={selectSx}
                            SelectDisplayProps={{ 'aria-labelledby': providerLabelId, 'aria-required': 'true' }}
                        >
                            <MenuItem value="" disabled>Escolha a operadora</MenuItem>
                            {options.map((provider) => (
                                <MenuItem key={provider.id} value={provider.id}>{provider.label}</MenuItem>
                            ))}
                        </Select>
                        {errors.provider_id && <FormHelperText sx={{ ml: 0 }}>{errors.provider_id}</FormHelperText>}
                    </FormControl>
                </div>

                {plans.length > 0 && (
                    <div className={styles.field}>
                        <span className={styles.label} id={planLabelId}>Plano</span>
                        <FormControl size="small" fullWidth disabled={disabled}>
                            <Select
                                labelId={planLabelId}
                                value={form.plan_id}
                                onChange={(e) => onFieldChange('plan_id', e.target.value)}
                                displayEmpty
                                sx={selectSx}
                                SelectDisplayProps={{ 'aria-labelledby': planLabelId }}
                            >
                                <MenuItem value="">Sem plano específico</MenuItem>
                                {plans.map((plan) => (
                                    <MenuItem key={plan.id} value={plan.id}>{plan.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>
                )}

                <div className={`${styles.field} ${showMore ? '' : styles.full}`}>
                    <label className={styles.label} htmlFor={`${idPrefix}-number`}>Número da carteirinha *</label>
                    <TextField
                        id={`${idPrefix}-number`}
                        value={form.card_number}
                        onChange={(e) => onFieldChange('card_number', e.target.value)}
                        disabled={disabled}
                        size="small"
                        fullWidth
                        autoFocus={autoFocusNumber}
                        placeholder="Como está impresso na carteirinha"
                        error={!!errors.card_number}
                        helperText={errors.card_number}
                        className={styles.input}
                        inputProps={{ maxLength: CARD_NUMBER_MAX }}
                    />
                </div>

                {showMore && (
                    <>
                        <div className={styles.field}>
                            <label className={styles.label} htmlFor={`${idPrefix}-valid`}>Vencimento em</label>
                            <TextField
                                id={`${idPrefix}-valid`}
                                type="date"
                                value={form.valid_until}
                                onChange={(e) => onFieldChange('valid_until', e.target.value)}
                                disabled={disabled}
                                size="small"
                                fullWidth
                                error={!!errors.valid_until}
                                helperText={errors.valid_until || 'Opcional'}
                                className={styles.input}
                                InputLabelProps={{ shrink: true }}
                            />
                        </div>
                        <div className={`${styles.field} ${styles.full}`}>
                            <label className={styles.label} htmlFor={`${idPrefix}-holder`}>Titular</label>
                            <TextField
                                id={`${idPrefix}-holder`}
                                value={form.holder_name}
                                onChange={(e) => onFieldChange('holder_name', e.target.value)}
                                disabled={disabled}
                                size="small"
                                fullWidth
                                placeholder="Deixe vazio quando a própria pessoa é a titular"
                                className={styles.input}
                                inputProps={{ maxLength: HOLDER_MAX }}
                            />
                        </div>
                        <div className={`${styles.field} ${styles.full}`}>
                            <label className={styles.label} htmlFor={`${idPrefix}-notes`}>Observações</label>
                            <TextField
                                id={`${idPrefix}-notes`}
                                value={form.notes}
                                onChange={(e) => onFieldChange('notes', e.target.value)}
                                disabled={disabled}
                                size="small"
                                fullWidth
                                multiline
                                rows={compact ? 2 : 3}
                                placeholder="Opcional. Carência, coparticipação, telefone da central"
                                className={styles.input}
                                inputProps={{ maxLength: NOTES_MAX }}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
